import type { AttendanceException, AttendanceRecord } from "@leave-system/shared";

type AttendanceStatus =
  | AttendanceRecord["status"]
  | "not_checked_in"
  | null
  | undefined;

export function getAttendanceStatusLabel(status: AttendanceStatus): string {
  switch (status) {
    case "checked_in":
      return "เข้างานแล้ว";
    case "completed":
      return "ออกงานแล้ว";
    case "missing_checkout":
      return "ขาดการออกงาน";
    default:
      return "ยังไม่ได้เข้างาน";
  }
}

export function getAttendanceStatusClass(status: AttendanceStatus): string {
  switch (status) {
    case "checked_in":
      return "bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-200";
    case "completed":
      return "bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-200";
    case "missing_checkout":
      return "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200";
    default:
      return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200";
  }
}

export function getAttendanceExceptionTypeLabel(
  type: AttendanceException["type"] | undefined
): string {
  switch (type) {
    case "missing_checkout":
      return "ลืมออกงาน";
    case "manual_entry":
      return "ลงเวลาย้อนหลัง";
    case "time_correction":
      return "แก้ไขเวลา";
    default:
      return "-";
  }
}

export function getAttendanceExceptionStatusLabel(
  status: AttendanceException["status"] | undefined
): string {
  switch (status) {
    case "pending":
      return "รอดำเนินการ";
    case "resolved":
      return "อนุมัติแล้ว";
    case "rejected":
      return "ปฏิเสธ";
    default:
      return "-";
  }
}

export function getAttendanceExceptionStatusClass(
  status: AttendanceException["status"] | undefined
): string {
  switch (status) {
    case "pending":
      return "bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-200";
    case "resolved":
      return "bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-200";
    case "rejected":
      return "bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-200";
    default:
      return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200";
  }
}

export function formatAttendanceDateTime(value?: string | null): string {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleString("th-TH", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function formatAttendanceMinutes(value?: number | null): string {
  if (value === null || value === undefined) {
    return "-";
  }
  if (value < 60) {
    return `${value} นาที`;
  }

  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  if (!minutes) {
    return `${hours} ชม.`;
  }

  return `${hours} ชม. ${minutes} นาที`;
}

export function toDateTimeLocalValue(value?: string | null): string {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60000);
  return localDate.toISOString().slice(0, 16);
}

export function fromDateTimeLocalValue(value: string): string | undefined {
  return value ? new Date(value).toISOString() : undefined;
}

export function getLocalTodayDateInput(): string {
  const date = new Date();
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60000);
  return localDate.toISOString().slice(0, 10);
}

export async function getCurrentBrowserLocation(): Promise<{
  latitude: number;
  longitude: number;
  accuracy: number;
}> {
  if (!navigator.geolocation) {
    throw new Error("เบราว์เซอร์นี้ไม่รองรับการระบุตำแหน่ง");
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        }),
      () => reject(new Error("ไม่สามารถอ่านตำแหน่งปัจจุบันได้")),
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  });
}
